import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Camera, Gauge, Trash, WarningCircle, X } from '@phosphor-icons/react';
import { Auswahl, FeldRaster, ImmoButton, InnenKarte, KartenTitel, SearchSelect, TextFeld, type SucheOption } from '../../../../../components/immobilien/ui';
import { supabase } from '../../../../../lib/supabase/client';
import { bereinigeSuchbegriff } from '../../../../../lib/kontakte/personenSuche';
import { hausAdresse, sortiereHaeuser, type Haus } from '../../../../../lib/objekte/haeuser';
import { deDatum } from '../../../../../lib/immobilien/objektDetail';
import { heuteIso } from '../../../../../lib/immobilien/format';
import { kontaktName } from '../../../../../lib/objekte/objektKontakte';
import { ZAEHLER_TYPEN, pruefeAblesung, zaehlerFehler, zaehlerPayload, type ZaehlerEntwurf } from '../../../../../lib/immobilien/zaehlerFormular';
import type { AblesungKern } from '../../../../../lib/zaehler/ablesungen';
import { korrigiereNachEinfuegen } from '../../../../../lib/zaehler/zaehlerApi';

const schliessen = (onClick: () => void) => (
  <button type="button" aria-label="Schließen" onClick={onClick} className="flex items-center justify-center cursor-pointer"
    style={{ width: 30, height: 30, borderRadius: '50%', border: 'none', background: 'var(--fill2)', color: 'var(--ink)' }}><X size={14} weight="bold" /></button>
);

type ZaehlerZeileVoll = Record<string, unknown> & { id: string; mieter?: { id: string; vorname: string | null; nachname: string | null; firma_name: string | null } | null };

/**
 * Zähler anlegen / bearbeiten im neuen Design – Felder wie im Zählerbereich (Nummer, Typ, Messeinheit,
 * Standort, Haus, Einheit, Mieter, Notizen). Das Objekt ist durch die Seite vorgegeben.
 * Beim Bearbeiten wird die vollständige Zeile geladen, damit Mieter und Notizen erhalten bleiben.
 */
export function ZaehlerEditor({ objekt, zaehlerId, einheiten, vorbelegung, onAbbrechen, onGespeichert }: {
  objekt: { id: string; objektnummer?: string | null };
  zaehlerId: string | null;
  einheiten: readonly string[];
  vorbelegung?: { einheitBezeichnung?: string; mieter?: SucheOption | null };
  onAbbrechen: () => void;
  onGespeichert: (meldung: string) => void;
}) {
  const listeId = useId();
  const [z, setZ] = useState<ZaehlerEntwurf>({
    zaehler_nummer: objekt.objektnummer ? `${objekt.objektnummer}-` : '', typ: 'strom', eigenerTyp: '', einheit: 'kWh', standort: '', haus_id: '',
    einheit_bezeichnung: vorbelegung?.einheitBezeichnung ?? '', mieter_id: vorbelegung?.mieter?.id ?? '', notizen: '',
  });
  const [mieter, setMieter] = useState<SucheOption | null>(vorbelegung?.mieter ?? null);
  const [haeuser, setHaeuser] = useState<Haus[]>([]);
  const [laedt, setLaedt] = useState(!!zaehlerId);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const set = (f: keyof ZaehlerEntwurf) => (v: string) => setZ(x => ({ ...x, [f]: v }));

  useEffect(() => {
    let aktiv = true;
    supabase.from('objekt_haeuser').select('*').eq('objekt_id', objekt.id).then(({ data }) => { if (aktiv) setHaeuser(sortiereHaeuser((data as Haus[] | null) ?? [])); });
    if (zaehlerId) {
      void supabase.from('zaehler').select('*, mieter:mieter_id(id, vorname, nachname, firma_name)').eq('id', zaehlerId).maybeSingle().then(({ data, error }) => {
        if (!aktiv) return;
        if (error || !data) { setFehler(`Zähler konnte nicht geladen werden${error ? `: ${error.message}` : ''}`); setLaedt(false); return; }
        const r = data as ZaehlerZeileVoll;
        const typ = String(r.typ ?? '');
        const bekannt = ZAEHLER_TYPEN.some(t => t.key === typ);
        const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));
        setZ({ zaehler_nummer: s(r.zaehler_nummer), typ: bekannt ? typ : '_eigen', eigenerTyp: bekannt ? '' : typ, einheit: s(r.einheit), standort: s(r.standort),
          haus_id: s(r.haus_id), einheit_bezeichnung: s(r.einheit_bezeichnung), mieter_id: s(r.mieter_id), notizen: s(r.notizen) });
        setMieter(r.mieter ? { id: r.mieter.id, label: kontaktName(r.mieter), typ: 'mieter' } : null);
        setLaedt(false);
      });
    }
    return () => { aktiv = false; };
  }, [objekt.id, zaehlerId]);

  const sucheMieter = useCallback(async (anfrage: string, _t: string | null, signal: AbortSignal): Promise<SucheOption[]> => {
    const m = `%${bereinigeSuchbegriff(anfrage).replace(/%/g, ' ')}%`;
    const { data, error } = await supabase.from('mieter').select('id, vorname, nachname, firma_name, einheit, objekt_id')
      .or(`vorname.ilike.${m},nachname.ilike.${m},firma_name.ilike.${m},email.ilike.${m}`).limit(8).abortSignal(signal);
    if (error) throw new Error(error.message);
    return ((data ?? []) as { id: string; vorname: string | null; nachname: string | null; firma_name: string | null; einheit: string | null; objekt_id: string | null }[])
      .map(x => ({ id: x.id, label: kontaktName(x), sub: [x.einheit, x.objekt_id === objekt.id ? 'dieses Objekt' : ''].filter(Boolean).join(' · ') || undefined, typ: 'mieter' }));
  }, [objekt.id]);

  const typWaehlen = (k: string) => {
    const t = ZAEHLER_TYPEN.find(x => x.key === k);
    setZ(x => ({ ...x, typ: k, einheit: t ? t.einheit : x.einheit }));
  };

  const speichere = async () => {
    const f = zaehlerFehler(z);
    if (f) { setFehler(f); return; }
    setLaeuft(true); setFehler(null);
    const payload = zaehlerPayload(z, objekt.id, haeuser.map(h => h.id), new Date().toISOString());
    const r = zaehlerId ? await supabase.from('zaehler').update(payload).eq('id', zaehlerId) : await supabase.from('zaehler').insert(payload);
    setLaeuft(false);
    if (r.error) { setFehler(r.error.message); return; }
    onGespeichert(zaehlerId ? 'Zähler gespeichert' : 'Zähler angelegt');
  };

  return (
    <InnenKarte style={{ boxShadow: 'inset 0 0 0 2px var(--acc)' }}>
      <KartenTitel icon={Gauge} rechts={schliessen(onAbbrechen)}>{zaehlerId ? 'Zähler bearbeiten' : 'Neuer Zähler'}</KartenTitel>
      {laedt ? <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Zähler wird geladen …</span> : (<>
        <Auswahl label="Typ *" wert={z.typ} onChange={typWaehlen} optionen={[...ZAEHLER_TYPEN.map(t => ({ key: t.key, label: t.label })), { key: '_eigen', label: 'Sonstiger …' }]} />
        <FeldRaster minBreite={170}>
          {z.typ === '_eigen' && <TextFeld label="Eigener Typ *" wert={z.eigenerTyp} onChange={set('eigenerTyp')} placeholder="z. B. Solar" />}
          <TextFeld label="Zählernummer *" wert={z.zaehler_nummer} onChange={set('zaehler_nummer')} placeholder="z. B. 1ESY1234567890" />
          <TextFeld label="Messeinheit" wert={z.einheit} onChange={set('einheit')} placeholder="kWh, m³, Liter …" />
          <TextFeld label="Standort" wert={z.standort} onChange={set('standort')} placeholder="z. B. Keller Raum 3" />
          <TextFeld label="Einheit / Wohnung" wert={z.einheit_bezeichnung} onChange={set('einheit_bezeichnung')} list={listeId} placeholder="z. B. 02 1.OG" />
        </FeldRaster>
        <datalist id={listeId}>{einheiten.map(e => <option key={e} value={e} />)}</datalist>
        {haeuser.length > 0 && (
          <Auswahl label="Haus" wert={z.haus_id || 'ohne'} onChange={k => set('haus_id')(k === 'ohne' ? '' : k)}
            optionen={[{ key: 'ohne', label: 'Ohne Haus (ganze Liegenschaft)' }, ...haeuser.map(h => ({ key: h.id, label: hausAdresse(h) ? `${h.bezeichnung} · ${hausAdresse(h)}` : h.bezeichnung }))]} />
        )}
        <SearchSelect label="Mieter (optional)" platzhalter="Name, Firma oder E-Mail …" suche={sucheMieter}
          onWahl={o => { setMieter(o); set('mieter_id')(o.id); }} ausgewaehlt={mieter} onEntfernen={() => { setMieter(null); set('mieter_id')(''); }} />
        <TextFeld label="Notizen (optional)" wert={z.notizen} onChange={set('notizen')} />
        {fehler && <span role="alert" style={{ fontSize: 13, fontWeight: 700, color: 'var(--bad)' }}>{fehler}</span>}
        <div className="flex justify-end" style={{ gap: 8 }}>
          <ImmoButton onClick={onAbbrechen}>Abbrechen</ImmoButton>
          <ImmoButton art="akzent" disabled={laeuft} onClick={() => { void speichere(); }}>{laeuft ? 'Speichert …' : zaehlerId ? 'Speichern' : 'Zähler anlegen'}</ImmoButton>
        </div>
        <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Objekt wechseln und Zähler deaktivieren: im Zählerbereich.</span>
      </>)}
    </InnenKarte>
  );
}

/**
 * Zählerstand erfassen im neuen Design – wie bisher mit Plausibilitätsprüfung und Foto.
 * Neu: Komma-Zahlen werden richtig gelesen, Nachträge rechnen gegen die frühere Ablesung,
 * und ein fehlgeschlagener Foto-Upload speichert nicht still ohne Foto.
 */
export function AblesungEditor({ zaehler, nutzerId, onAbbrechen, onGespeichert }: {
  zaehler: { id: string; typ: string | null; zaehler_nummer: string | null; einheit: string | null };
  nutzerId: string | null;
  onAbbrechen: () => void;
  onGespeichert: (meldung: string) => void;
}) {
  const [datum, setDatum] = useState(heuteIso());
  const [wert, setWert] = useState('');
  const [notizen, setNotizen] = useState('');
  const [ablesungen, setAblesungen] = useState<AblesungKern[]>([]);
  const [foto, setFoto] = useState<File | null>(null);
  const [vorschau, setVorschau] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const datei = useRef<HTMLInputElement>(null);
  const einheit = zaehler.einheit ?? '';

  useEffect(() => {
    let aktiv = true;
    supabase.from('ablesungen').select('id, wert, datum, verbrauch, created_at').eq('zaehler_id', zaehler.id).order('datum', { ascending: false }).limit(24)
      .then(({ data }) => {
        if (!aktiv) return;
        setAblesungen(((data ?? []) as { id: string; wert: number | string; datum: string; verbrauch: number | string | null; created_at: string | null }[])
          .map(a => ({ id: a.id, wert: Number(a.wert), datum: a.datum.slice(0, 10), verbrauch: a.verbrauch === null ? null : Number(a.verbrauch), created_at: a.created_at })));
      });
    return () => { aktiv = false; };
  }, [zaehler.id]);

  useEffect(() => {
    if (!foto) { setVorschau(null); return; }
    const url = URL.createObjectURL(foto);
    setVorschau(url);
    return () => URL.revokeObjectURL(url);
  }, [foto]);

  const p = pruefeAblesung(wert, datum, ablesungen, einheit);

  const speichere = async () => {
    if (p.wert === null) { setFehler('Bitte einen gültigen Zählerstand eingeben.'); return; }
    setLaeuft(true); setFehler(null);
    try {
      let fotoUrl: string | null = null;
      if (foto) {
        const pfad = `${zaehler.id}/${Date.now()}.${foto.name.split('.').pop() || 'jpg'}`;
        const up = await supabase.storage.from('zaehler-fotos').upload(pfad, foto);
        if (up.error) throw new Error(`Foto konnte nicht hochgeladen werden (${up.error.message}). Foto entfernen oder erneut versuchen.`);
        fotoUrl = supabase.storage.from('zaehler-fotos').getPublicUrl(pfad).data.publicUrl;
      }
      const { data: neu, error } = await supabase.from('ablesungen').insert({
        zaehler_id: zaehler.id, datum, wert: p.wert, verbrauch: p.verbrauch, foto_url: fotoUrl, notizen: notizen.trim() || null, erfasst_von: nutzerId,
      }).select('id, created_at').single();
      if (error) throw new Error(error.message);
      // Nachtrag: Verbrauch der folgenden Ablesung anpassen (wie im Zählerbereich)
      const n = neu as { id: string; created_at: string | null };
      await korrigiereNachEinfuegen([...ablesungen, { id: n.id, datum, wert: p.wert, verbrauch: p.verbrauch, created_at: n.created_at }], n.id);
      onGespeichert('Zählerstand erfasst');
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <InnenKarte style={{ boxShadow: 'inset 0 0 0 2px var(--acc)' }}>
      <KartenTitel icon={Gauge} rechts={schliessen(onAbbrechen)}>Zählerstand erfassen · {zaehler.zaehler_nummer || zaehler.typ || 'Zähler'}</KartenTitel>
      {p.vorher
        ? <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Letzter Stand vor diesem Datum: {p.vorher.wert.toLocaleString('de-DE')} {einheit} am {deDatum(p.vorher.datum)}</span>
        : <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{ablesungen.length ? 'Keine frühere Ablesung vor diesem Datum.' : 'Erste Ablesung für diesen Zähler.'}</span>}
      <FeldRaster minBreite={170}>
        <TextFeld label="Datum" type="date" wert={datum} onChange={setDatum} />
        <TextFeld label="Zählerstand *" wert={wert} onChange={setWert} inputMode="decimal" suffix={einheit || undefined} placeholder="z. B. 12.345,6" />
      </FeldRaster>
      {p.verbrauch !== null && (
        <span className="immo-zahl" style={{ fontSize: 13, fontWeight: 700, color: p.verbrauch < 0 ? 'var(--bad)' : 'var(--ink2)' }}>
          Verbrauch seit {deDatum(p.vorher?.datum)}: {p.verbrauch.toLocaleString('de-DE')} {einheit}
        </span>
      )}
      {p.warnung && (
        <span role="status" className="flex items-start" style={{ gap: 8, padding: '10px 12px', borderRadius: 14, background: 'var(--warn-soft)', color: 'var(--warn)', fontSize: 13, fontWeight: 600 }}>
          <WarningCircle weight="fill" size={17} style={{ flexShrink: 0, marginTop: 1 }} />{p.warnung}
        </span>
      )}
      <TextFeld label="Notizen (optional)" wert={notizen} onChange={setNotizen} />
      <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
        <input ref={datei} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => setFoto(e.target.files?.[0] ?? null)} />
        {vorschau
          ? (<>
            <img src={vorschau} alt="Foto des Zählerstands" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 12 }} />
            <ImmoButton groesse={36} art="bad" icon={Trash} onClick={() => { setFoto(null); if (datei.current) datei.current.value = ''; }}>Foto entfernen</ImmoButton>
          </>)
          : <ImmoButton groesse={36} icon={Camera} onClick={() => datei.current?.click()}>Foto hinzufügen</ImmoButton>}
      </div>
      {fehler && <span role="alert" style={{ fontSize: 13, fontWeight: 700, color: 'var(--bad)' }}>{fehler}</span>}
      <div className="flex justify-end" style={{ gap: 8 }}>
        <ImmoButton onClick={onAbbrechen}>Abbrechen</ImmoButton>
        <ImmoButton art="akzent" disabled={laeuft || !wert.trim()} onClick={() => { void speichere(); }}>{laeuft ? 'Speichert …' : 'Stand speichern'}</ImmoButton>
      </div>
    </InnenKarte>
  );
}
